import { CameraOff, ImageOff, MicOff } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import CallRenderScreen from "@/features/vidyo-call/components/call-render-screen";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { useHasWebRTCPermission } from "../api/has-webrtc-permission";
import { useManageDevicesStatus } from "../api/manage-devices-status";
import { isWebRTCSupported } from "../utils/is-webrtc-supported";

import { DeviceStatus } from "./device-status";
import { SelectDevice } from "./select-device";

export function PreviewDevice() {
  const { requestCameraPermission, requestMicrophonePermission } =
    useHasWebRTCPermission();
  const {
    isCameraMuted,
    isVidyoReady,
    hasCameraPermission,
    nbOfCameras,
    hasMicrophonePermission,
  } = useVidyoStore(
    useShallow((state) => ({
      isCameraMuted: state.camera.shouldBeMuted,
      isVidyoReady: state.isVidyoReady,
      hasCameraPermission: state.camera.hasPermission,
      nbOfCameras: state.camera.available.length,
      nbOfMicrophones: state.microphone.available.length,
      hasMicrophonePermission: state.microphone.hasPermission,
    })),
  );
  useManageDevicesStatus();
  if (!isVidyoReady) {
    return (
      <div className="flex-1">
        <div className="flex aspect-video flex-col items-center justify-center rounded-2xl bg-foreground p-4 dark:bg-card"></div>
        <div className="h-11" />
      </div>
    );
  }

  if (!isWebRTCSupported) {
    return (
      <div className="flex aspect-video flex-1 flex-col items-center justify-center rounded-2xl border border-border bg-foreground p-4">
        <ImageOff className="size-8 text-white" />
        <p className="text-lg text-white">Camera access disallowed</p>
      </div>
    );
  }

  if (isCameraMuted) {
    return (
      <div className="flex-1">
        <div className="relative flex aspect-video flex-col items-center justify-center rounded-2xl bg-foreground p-4 dark:bg-card">
          <CameraOff className="mb-2 size-8 text-white" />
          <p className="text-lg text-white">Camera disabled</p>

          <div className="absolute bottom-4 mt-3">
            <DeviceStatus />
          </div>
        </div>
        <div className="mt-3">
          <SelectDevice />
        </div>
      </div>
    );
  }

  if (nbOfCameras === 0) {
    return (
      <div className="flex-1">
        <div className="relative flex flex-col overflow-hidden rounded-2xl">
          <div className="flex aspect-video flex-col items-center justify-center rounded-2xl border border-border bg-foreground p-4 dark:bg-card">
            <CameraOff className="size-8 text-white" />
            <p className="text-lg text-white">No camera(s) available</p>
          </div>
          <div className="absolute inset-x-0 bottom-4 mt-3">
            <div className="flex justify-center">
              <DeviceStatus />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <SelectDevice />
        </div>
      </div>
    );
  }

  if (!hasCameraPermission) {
    return (
      <div className="flex aspect-video flex-1 flex-col items-center justify-center rounded-2xl border border-border bg-foreground p-4 dark:bg-card">
        <CameraOff className="mb-2 size-8 text-white" />
        <p className="text-lg text-white">
          Do you want people to see you in the meeting?
        </p>
        <p className="mb-4 max-w-md text-center text-sm text-white">
          Cannot access your devices. Please ensure you have granted permission
          in your browser settings (URL bar) and that no other application is
          using your camera.
        </p>
        <button
          onClick={requestCameraPermission}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Request Permissions
        </button>
      </div>
    );
  }

  if (!hasMicrophonePermission) {
    return (
      <div className="flex aspect-video flex-1 flex-col items-center justify-center rounded-2xl border border-border bg-foreground p-4 dark:bg-card">
        <MicOff className="mb-2 size-8 text-white" />
        <p className="text-lg text-white">
          Do you want people to hear you in the meeting?
        </p>
        <p className="mb-4 max-w-md text-center text-sm text-white">
          Cannot access your devices. Please ensure you have granted permission
          in your browser settings (URL bar) and that no other application is
          using your microphone.
        </p>
        <button
          onClick={requestMicrophonePermission}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Request Permissions
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <div className="relative flex flex-col overflow-hidden rounded-2xl">
        <div className="aspect-video bg-foreground dark:bg-card">
          <CallRenderScreen />
        </div>
        <div className="absolute inset-x-0 bottom-4 mt-3">
          <div className="flex justify-center">
            <DeviceStatus />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <SelectDevice />
      </div>
    </div>
  );
}
